import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { money } from "@/lib/format";
import { API } from "@/lib/api";

let logoVer = Date.now();
let logoCache = null;
export const logoUrl = () => `${API}/public/receipt-logo?v=${logoVer}`;
export const bumpLogo = () => { logoVer = Date.now(); logoCache = null; };

export const sigName = (r) => { const n = (r?.received_by_name || "").trim(); return /^(master )?admin$/i.test(n) ? "" : n; };
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function receiptWhen(r) {
  const [y, m, d] = String(r?.date || "").split("-").map(Number);
  if (!y || !m || !d) return r?.date || "";
  const t = r.created_at ? new Date(r.created_at).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" }) : "";
  return `${MON[m - 1]} ${d}, ${y}${t && t !== "Invalid Date" ? `, ${t}` : ""}`;
}
export const SIG_STYLES = [{ value: "flowing", label: "Flowing" }, { value: "elegant", label: "Elegant" }, { value: "handwritten", label: "Handwritten" }];
export const sigUrl = (n, style = "flowing") => `${API}/public/signature?name=${encodeURIComponent(n)}&style=${style || "flowing"}`;

async function loadImg(url) {
  try {
    const blob = await (await fetch(url)).blob();
    const data = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob); });
    const dims = await new Promise((res) => { const im = new Image(); im.onload = () => res([im.naturalWidth, im.naturalHeight]); im.onerror = () => res([1, 1]); im.src = data; });
    return { data, w: dims[0], h: dims[1], fmt: blob.type.includes("png") ? "PNG" : "JPEG" };
  } catch { return null; }
}

export async function loadLogo() {
  if (!logoCache) logoCache = await loadImg(logoUrl());
  return logoCache;
}
const loadSig = (r) => (sigName(r) ? loadImg(sigUrl(sigName(r), r.received_by_style)) : Promise.resolve(null));

export const PAPERS = [
  { value: "letter", label: "Letter (8.5 × 11 in)", pt: [612, 792], css: "8.5in 11in", margin: "0.6in", font: 12 },
  { value: "a4", label: "A4 (210 × 297 mm)", pt: [595.28, 841.89], css: "210mm 297mm", margin: "15mm", font: 12 },
  { value: "half", label: "Half page (5.5 × 8.5 in)", pt: [396, 612], css: "5.5in 8.5in", margin: "0.35in", font: 9.5, m: 26 },
  { value: "quarter", label: "Quarter page (4.25 × 5.5 in)", pt: [306, 396], css: "4.25in 5.5in", margin: "0.25in", font: 7, m: 18, big: 6 },
];
const paper = (v) => PAPERS.find((p) => p.value === v) || PAPERS[0];
const info = (r) => [["Receipt no.", r.number], ["Date", receiptWhen(r)], ["Paid by", r.payer_name], ...(r.payer_phone ? [["Phone", r.payer_phone]] : []), ["Church", r.church_name], ["For", r.payment_label], ...(r.event_name ? [["Event", r.event_name]] : [])];

export function receiptPDF(r, size, logo, sig) {
  const p = paper(size);
  const doc = new jsPDF({ unit: "pt", format: p.pt });
  const W = p.pt[0];
  const m = p.m || 44;
  const f = p.font;
  const big = p.big || 10;
  const head = r.heading || r.destination;
  const rb = sigName(r);
  const sh = sig ? f * 3 : 0;
  const sw = sig ? Math.min((sh * sig.w) / sig.h, (W - m * 2) * 0.45) : 0;
  const lh = logo ? f * 5.5 : 0;
  const lw = logo ? Math.min((lh * logo.w) / logo.h, W * 0.5) : 0;
  const lhh = logo ? (lw * logo.h) / logo.w : 0;
  if (logo) { doc.addImage(logo.data, logo.fmt, (W - lw) / 2, m - f * 0.5, lw, lhh); }
  const t = m + lhh + f * 0.6;
  doc.setFont("helvetica", "bold").setFontSize(f + big).text(head, W / 2, t + f, { align: "center" });
  doc.setFontSize(f + 4).text("RECEIPT", m, t + f * 3);
  doc.setFontSize(f + 2).text(r.number, W - m, t + f * 3, { align: "right" });
  doc.setFont("helvetica", "normal").setFontSize(f);
  autoTable(doc, {
    startY: t + f * 4, margin: { left: m, right: m }, theme: "plain", styles: { fontSize: f, cellPadding: 2 },
    body: info(r), columnStyles: { 0: { cellWidth: f * 7.5, textColor: [100, 116, 139] }, 1: { fontStyle: "bold" } },
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + f, margin: { left: m, right: m, top: m }, theme: "grid",
    styles: { fontSize: f, cellPadding: f * 0.4, lineColor: [200, 200, 200], textColor: [20, 20, 20] },
    headStyles: { fillColor: [241, 245, 249], fontStyle: "bold" },
    head: [["#", "Name", "Amount"]], body: r.lines.map((l, i) => [i + 1, l.name, money(l.amount)]),
    foot: [["", "TOTAL", money(r.total)]], showFoot: "lastPage", footStyles: { fillColor: [255, 255, 255], fontStyle: "bold", textColor: [20, 20, 20] },
    columnStyles: { 0: { cellWidth: f * 2.5 }, 2: { halign: "right", cellWidth: f * 8 } },
    didParseCell: (d) => { if (d.column.index === 2) d.cell.styles.halign = "right"; },
  });
  let y = doc.lastAutoTable.finalY + f * 1.6;
  const H = p.pt[1];
  if (y + f * 6 + sh > H - m) { doc.addPage(); y = m + f * 2; }
  if (r.notes) {
    const nl = doc.splitTextToSize(`Note: ${r.notes}`, W - m * 2);
    const nh = nl.length * f * 1.15;
    if (y + nh + f * 5 + sh > H - m) { doc.addPage(); y = m + f * 2; }
    doc.text(nl, m, y); y += nh + f;
  }
  y += f * 2 + sh;
  if (sig) doc.addImage(sig.data, sig.fmt, m, y - f * 1.5 - (sw * sig.h) / sig.w, sw, (sw * sig.h) / sig.w);
  if (rb) doc.setFont("helvetica", "bold").setFontSize(f).text(rb, m, y - f * 0.4);
  doc.setFont("helvetica", "normal");
  doc.line(m, y, m + (W - m * 2) * 0.45, y);
  doc.setFontSize(f - 1).text("Received by", m, y + f);
  doc.text("Thank you. Kommol tata!", W - m, y + f, { align: "right" });
  const pages = doc.getNumberOfPages();
  for (let i = 2; i <= pages; i += 1) { doc.setPage(i); doc.setFontSize(f - 1).text(`Receipt ${r.number} · page ${i} of ${pages}`, W - m, m * 0.6, { align: "right" }); }
  return doc;
}

export const downloadReceiptPDF = async (r, size) => receiptPDF(r, size, await loadLogo(), await loadSig(r)).save(`Receipt-${r.number}-${size}.pdf`);

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

export function receiptHTML(r, size, logoSrc = logoUrl(), sigSrc = sigName(r) ? sigUrl(sigName(r), r.received_by_style) : "") {
  const p = paper(size);
  const rows = r.lines.map((l, i) => `<tr><td>${i + 1}</td><td>${esc(l.name)}</td><td class="r">${money(l.amount)}</td></tr>`).join("");
  const meta = info(r).map(([k, v]) => `<tr><td class="k">${k}</td><td><b>${esc(v)}</b></td></tr>`).join("");
  return `<!doctype html><html><head><title>Receipt ${esc(r.number)}</title><style>
@page{size:${p.css};margin:${p.margin}}*{box-sizing:border-box}body{font-family:Helvetica,Arial,sans-serif;font-size:${p.font}pt;color:#141414;margin:0}
.logo{text-align:center;margin:0 0 6pt}.logo img{height:${p.font * 5.5}pt;max-width:50%;width:auto;object-fit:contain}
.top{display:flex;justify-content:space-between;align-items:baseline}.head{text-align:center;font-size:${p.font + (p.big || 10)}pt;font-weight:bold;margin:0 0 6pt}h1{font-size:${p.font + 4}pt;margin:0}.no{font-size:${p.font + 2}pt;font-weight:bold}
.org{margin:4pt 0 10pt}.meta td{padding:1.5pt 4pt 1.5pt 0}.k{color:#64748b;width:7.5em}
table.lines{width:100%;border-collapse:collapse;margin-top:10pt}table.lines td,table.lines th{border:1px solid #c8c8c8;padding:${p.font * 0.4}pt;text-align:left}
table.lines th{background:#f1f5f9}.r{text-align:right!important}tfoot td{font-weight:bold}tr{page-break-inside:avoid}
.sig{margin-top:${p.font * 3}pt;display:flex;justify-content:space-between;align-items:flex-end;font-size:${p.font - 1}pt}.sigbox{width:45%}.sigbox img{display:block;height:${p.font * 3}pt;max-width:100%;width:auto;object-fit:contain}.sigbox b{display:block;font-size:${p.font}pt;padding-bottom:3pt}.line{border-top:1px solid #141414;padding-top:2pt}
</style></head><body>${logoSrc ? `<div class="logo"><img src="${esc(logoSrc)}" alt="Logo"></div>` : ""}<div class="head">${esc(r.heading || r.destination)}</div><div class="top"><h1>RECEIPT</h1><span class="no">${esc(r.number)}</span></div><div class="org"></div>
<table class="meta">${meta}</table><table class="lines"><thead><tr><th style="width:2.5em">#</th><th>Name</th><th class="r" style="width:8em">Amount</th></tr></thead>
<tbody>${rows}</tbody><tfoot><tr><td></td><td>TOTAL</td><td class="r">${money(r.total)}</td></tr></tfoot></table>
${r.notes ? `<p>Note: ${esc(r.notes)}</p>` : ""}<div class="sig"><div class="sigbox">${sigSrc ? `<img src="${esc(sigSrc)}" alt="Signature">` : ""}${sigName(r) ? `<b>${esc(sigName(r))}</b>` : ""}<div class="line">Received by</div></div><div>Thank you. Kommol tata!</div></div></body></html>`;
}

export async function printReceipt(r, size) {
  const [logo, sig] = await Promise.all([loadLogo(), loadSig(r)]);
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(f);
  f.contentDocument.open();
  f.contentDocument.write(receiptHTML(r, size, logo?.data || "", sig?.data || ""));
  f.contentDocument.close();
  setTimeout(() => { f.contentWindow.focus(); f.contentWindow.print(); setTimeout(() => { f.remove(); window.focus(); }, 1000); }, 250);
}
